import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ChallengeCard from '../components/ChallengeCard';
import Accordion from '../components/Accordion';
import { ChallengeService, FirebaseService, logger } from '@fitzaa/shared';
import { ChallengeWithEligibility, User as SharedUser } from '@fitzaa/shared';
import { firebaseConfig, getEnvironment } from '../config/firebase';
import { joinChallenge as joinChallengeWeb } from '../services/WebCloudFunctionsService';
import LoggingConfigModal from '../components/LoggingConfig';
import Header from '../components/Header';
import VideoPlayer from '../components/VideoPlayer';

// (reverted) callable join removed; use shared services

interface MenuItemProps {
  label: string;
  icon: string;
  onClick: () => void;
  theme: any;
  highlight?: boolean;
}

type ChallengeFilter = 'all' | 'global' | 'personal' | 'organization';

const MenuItem: React.FC<MenuItemProps> = ({ label, icon, onClick, theme, highlight = false }) => {
  return (
    <li>
      <button
        onClick={onClick}
        style={{
          width: '100%',
          padding: '16px 20px',
          backgroundColor: 'transparent',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: theme.typography.body.fontSize,
          fontFamily: theme.fonts.primary,
          color: highlight ? theme.colors.primary : theme.colors.text,
          fontWeight: highlight ? 'bold' : 'normal',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.surface;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <span>{label}</span>
      </button>
    </li>
  );
};

const HomeScreen: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Add refs for immediate native scrolling
  const scrollViewRef = useRef<HTMLDivElement>(null);
  const challengesSectionRef = useRef<HTMLDivElement>(null);
  const lastFetchedUserId = useRef<string | null>(null);

  // Detect screen size for responsive grid layout
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1200);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1200);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1200);
      setIsLargeScreen(window.innerWidth >= 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get role emoji
  const getRoleEmoji = (role?: string) => {
    switch (role) {
      case 'super_admin':
        return '👑';
      case 'org_admin':
        return '⚡';
      case 'team_lead':
        return '🎯';
      case 'member':
      default:
        return '👤';
    }
  };

  // Helper function to get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const [allChallenges, setAllChallenges] = useState<ChallengeWithEligibility[]>([]);
  const [activeChallenges, setActiveChallenges] = useState<ChallengeWithEligibility[]>([]);
  const [userParticipations, setUserParticipations] = useState<any>({}); // Add participations state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentExpanded, setCurrentExpanded] = useState(true);
  const [recommendationsExpanded, setRecommendationsExpanded] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<ChallengeFilter>('all');
  const [showLoggingConfig, setShowLoggingConfig] = useState(false);
  const [userStreak, setUserStreak] = useState<number>(0);

  // Dashboard-level video player state (shared popout for all cards)
  const [videoChallenge, setVideoChallenge] = useState<ChallengeWithEligibility | null>(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'org_admin' || user?.role === 'team_admin';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Component rendered with user: ${user?.id}

  const convertToSharedUser = (webUser: any): SharedUser => {
    // Merge profile data with main user data if profile exists
    const userData = webUser.profile ? { ...webUser, ...webUser.profile } : webUser;

    const sharedUser = {
      id: userData.id,
      email: userData.email || '',
      displayName: userData.displayName || '',
      profileImageURL: userData.photoURL || undefined,
      joinedDate: new Date().toISOString(),
      role: userData.role || 'member',
      streak: 0,
      totalPoints: 0,
      organizationId: userData.organizationId,
      organizationName: userData.organizationName,
      teamId: userData.teamId,
      teamName: userData.teamName,
      familyStatus: userData.familyStatus,
      familyMemberOf: userData.familyMemberOf,
      authProvider: (userData.authProvider === 'azure' ? 'azure' :
        userData.authProvider === 'google' ? 'google' : 'email') as 'azure' | 'google' | 'email',
    };

    return sharedUser;
  };

  // Calculate overall user streak from all active challenges
  const calculateUserStreak = useCallback(async () => {
    if (!user?.id) return;

    try {
      const sharedUser = convertToSharedUser(user);
      const firebaseService = new FirebaseService(firebaseConfig, getEnvironment());
      let maxStreak = 0;

      // Use existing activeChallenges state instead of fetching again
      if (activeChallenges.length === 0) {
        console.log('📝 No active challenges found, setting streak to 0');
        setUserStreak(0);
        return;
      }

      // Calculate streak for each active challenge
      for (const challenge of activeChallenges) {
        if (challenge && challenge.id) {
          const participation = await firebaseService.getParticipation(user.id, challenge.id);
          console.log(`🔍 Participation for challenge ${challenge.id}:`, participation);

          if (participation && participation.dailyCompletions) {
            // OPTIMIZED: Use stored participation values when available for faster processing
            // For streak calculation, we still need dailyCompletions, but we can optimize other operations

            // Handle object format with date keys
            const dailyCompletionsArray = Object.values(participation.dailyCompletions);
            const completedDays = dailyCompletionsArray
              .filter(dc => dc && dc.isCompleted)
              .map(dc => new Date(dc.date))
              .sort((a, b) => b.getTime() - a.getTime());

            console.log(`📅 Found ${completedDays.length} completed days for challenge ${challenge.id}`);
            console.log(`📅 Completed days:`, completedDays.map(d => d.toISOString().split('T')[0]));

            if (completedDays.length > 0) {
              let currentStreak = 0;
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              console.log(`📅 Today's date:`, today.toISOString().split('T')[0]);

              // Check consecutive days from today backwards
              for (let i = 0; i < 30; i++) { // Check up to 30 days back
                const checkDate = new Date(today);
                checkDate.setDate(today.getDate() - i);
                checkDate.setHours(0, 0, 0, 0);

                const hasCompletion = completedDays.some(day => {
                  const dayDate = new Date(day);
                  dayDate.setHours(0, 0, 0, 0);
                  return dayDate.getTime() === checkDate.getTime();
                });

                console.log(`📅 Checking date ${checkDate.toISOString().split('T')[0]}: ${hasCompletion ? '✅' : '❌'}`);

                if (hasCompletion) {
                  currentStreak++;
                } else {
                  break;
                }
              }

              maxStreak = Math.max(maxStreak, currentStreak);
              console.log(`🔥 Challenge ${challenge.id} streak: ${currentStreak}, max so far: ${maxStreak}`);
            }
          } else {
            console.log(`📝 No daily completions found for challenge ${challenge.id}`);
          }
        }
      }

      setUserStreak(maxStreak);
      console.log('🔥 Final calculated user streak:', maxStreak);
    } catch (error) {
      console.error('❌ Error calculating user streak:', error);
      setUserStreak(0);
    }
  }, [user, activeChallenges]); // Add activeChallenges as dependency

  const fetchChallenges = useCallback(async (forceRefresh: boolean = false) => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Check if we've already fetched data for this user (unless forced refresh)
    if (!forceRefresh && lastFetchedUserId.current === user.id && allChallenges.length > 0) {
      console.log('🔄 Data already fetched for user, skipping fetch');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching challenges for user:', user.id);

      const sharedUser = convertToSharedUser(user);

      const firebaseService = new FirebaseService(firebaseConfig, getEnvironment());
      const challengeService = new ChallengeService(firebaseService);

      const [allChallengesData, active, participations] = await Promise.all([
        challengeService.getUserChallenges(user.id, sharedUser),
        challengeService.getActiveChallenges(user.id, sharedUser),
        firebaseService.loadUserParticipations(user.id, sharedUser)
      ]);

      logger.challenge.loading(`Fetched challenges: ${allChallengesData.length} total, ${active.length} active, ${participations.length} participations`);

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/dd95de8b-4aae-4806-ab49-a2b65a05ac50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'HomeScreen.tsx:256',
          message: 'Challenges fetched after join',
          data: {
            totalChallenges: allChallengesData.length,
            activeChallenges: active.length,
            participations: participations.length,
            participationStatuses: participations.map((p: any) => ({ challengeId: p.challengeId, status: p.status, isActive: p.isActive }))
          },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'F'
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
      }).catch(() => { });
      // #endregion

      // Convert participations to a map for easy lookup
      const participationsMap: any = {};
      participations.forEach((participation: any) => {
        participationsMap[participation.challengeId] = participation;
      });

      setAllChallenges(allChallengesData);
      setActiveChallenges(active);
      setUserParticipations(participationsMap);

      // Update the last fetched user ID
      lastFetchedUserId.current = user.id;

      // Streak calculation is now handled by separate useEffect
    } catch (err: any) {
      console.error('❌ Error fetching challenges:', err);
      setError(err.message || 'Failed to load challenges');
    } finally {
      setLoading(false);
    }
  }, [user, allChallenges.length]); // Add allChallenges.length to dependencies

  // Single useEffect to fetch challenges when user changes
  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  // Separate effect for calculating user streak when challenges or participations change
  useEffect(() => {
    if (user?.id && activeChallenges.length > 0) {
      calculateUserStreak();
    }
  }, [user?.id, activeChallenges, userParticipations, calculateUserStreak]);

  // Reset fetch tracking when user changes
  useEffect(() => {
    if (user?.id !== lastFetchedUserId.current) {
      lastFetchedUserId.current = null;
    }
  }, [user?.id]);

  const handleJoinChallenge = async (challengeId: string) => {
    if (!user?.id) {
      alert('You must be logged in to join challenges');
      return;
    }

    try {
      console.log('🔄 Joining challenge via Cloud Function:', challengeId);
      const result = await joinChallengeWeb(challengeId) as any;

      if (result?.success) {
        console.log('✅ Successfully joined challenge:', challengeId);

        // Force refresh challenges to update the lists after joining
        await fetchChallenges(true);

        // Navigate to challenge progress screen instead of showing popup
        navigate(`/challenge-progress/${challengeId}`);
      } else {
        console.error('❌ Failed to join challenge:', result?.error);
        alert(result?.error || 'Failed to join challenge');
      }
    } catch (error) {
      console.error('❌ Error joining challenge:', error);
      alert('Failed to join challenge. Please try again.');
    }
  };

  const handleViewProgress = (challengeId: string) => {
    console.log('🌐 Web HomeScreen: Navigating to challenge progress:', challengeId);
    navigate(`/challenge-progress/${challengeId}`);
  };

  const handleProfilePress = () => {
    console.log('🌐 Web HomeScreen: Profile button pressed');
    navigate('/user-settings');
  };

  const handleMenuPress = () => {
    console.log('🌐 Web HomeScreen: Menu button pressed');
    setMenuOpen(!menuOpen);
  };

  // Memoize filtered recommendations to prevent recalculation
  const filteredRecommendations = useMemo(() => {
    const unjoinedChallenges = allChallenges.filter(challenge =>
      !activeChallenges.some(active => active.id === challenge.id)
    );

    // Filter out expired challenges
    const now = new Date();
    const nonExpiredChallenges = unjoinedChallenges.filter(challenge => {
      const endDate = new Date(challenge.endDate);
      return now <= endDate;
    });

    switch (selectedFilter) {
      case 'global':
        return nonExpiredChallenges.filter(c => c.level === 'global');
      case 'personal':
        return nonExpiredChallenges.filter(c => c.level === 'personal');
      case 'organization':
        return nonExpiredChallenges.filter(c => c.level === 'organization');
      default:
        return nonExpiredChallenges;
    }
  }, [allChallenges, activeChallenges, selectedFilter]);

  // Memoize stats calculations to prevent recalculation on every render
  const stats = useMemo(() => {
    const availableCount = allChallenges.filter(challenge =>
      !activeChallenges.some(active => active.id === challenge.id)
    ).length;

    const activeCount = activeChallenges.length;

    return {
      available: availableCount,
      active: activeCount,
      streak: userStreak
    };
  }, [allChallenges, activeChallenges, userStreak]);

  // Check if user belongs to an organization
  const hasOrganization = useMemo(() => {
    return !!(user?.organizationId || user?.organizationName);
  }, [user]);

  const handleCardVideoPlay = useCallback((challenge: ChallengeWithEligibility) => {
    setVideoChallenge(challenge);
    setShowVideoPlayer(true);
  }, []);

  const handleDashboardVideoClose = useCallback(() => {
    setShowVideoPlayer(false);
    setVideoChallenge(null);
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: theme.colors.background,
        color: theme.colors.text
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading...</div>
          <div>Loading your challenges...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: theme.colors.background,
        color: theme.colors.text
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px', color: theme.colors.error }}>Error</div>
          <div>{error}</div>
          <button
            onClick={() => fetchChallenges(true)}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: theme.colors.primary,
              color: theme.colors.primaryText,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontFamily: theme.fonts.primary
    }}>
      {/* Header */}
      <Header
        title="Wellness Challenges"
        showBack={false}
        showMenu={true}
        showProfile={true}
        onMenu={handleMenuPress}
        onProfile={handleProfilePress}
      />

      {/* Side Menu */}
      {menuOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '280px',
          height: '100vh',
          backgroundColor: theme.colors.surface,
          borderRight: `1px solid ${theme.colors.border}`,
          zIndex: 200,
          padding: '24px 0',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{ padding: '0 24px 24px', borderBottom: `1px solid ${theme.colors.border}` }}>
            <div style={{
              fontSize: theme.typography.h2.fontSize,
              fontWeight: theme.typography.h2.fontWeight,
              marginBottom: '8px',
              color: theme.colors.text
            }}>
              {user?.displayName || 'User'}
            </div>
            <div style={{
              fontSize: theme.typography.body.fontSize,
              color: theme.colors.textSecondary
            }}>
              {user?.email}
            </div>
          </div>

          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: '24px 0',
            color: theme.colors.text
          }}>
            <MenuItem
              label="Home"
              icon="🏠"
              onClick={() => setMenuOpen(false)}
              theme={theme}
              highlight={true}
            />
            {isAdmin && (
              <MenuItem
                label="Admin Dashboard"
                icon="⚙️"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin');
                }}
                theme={theme}
              />
            )}
            <MenuItem
              label="Settings"
              icon="🔧"
              onClick={() => {
                setMenuOpen(false);
                navigate('/user-settings');
              }}
              theme={theme}
            />
            <MenuItem
              label="Sign Out"
              icon="🚪"
              onClick={handleLogout}
              theme={theme}
            />
          </ul>
        </div>
      )}

      {/* Main Content */}
      <main
        ref={scrollViewRef}
        style={{
          padding: isMobile ? '8px' : '24px',
          maxWidth: isMobile ? '100%' : '1200px',
          margin: isMobile ? '0' : '0 auto',
          minHeight: '100vh',
          overflowY: 'auto',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Hero Section */}
          <div
            className="hero-section"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: isMobile ? '12px' : '20px',
              padding: isMobile ? '12px 16px' : '24px 32px',
              marginBottom: isMobile ? '12px' : '32px',
              color: 'white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: isMobile ? 'center' : 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: isMobile ? '10px' : '16px',
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
            <h2 style={{
                fontSize: isMobile ? theme.typography.h2.fontSize : '28px',
              fontWeight: theme.typography.h2.fontWeight,
              margin: '0 0 6px 0',
              letterSpacing: '-0.5px',
              backgroundColor: 'transparent',
                lineHeight: isMobile ? '1.3' : '1.4'
            }}>
              {getGreeting()}, {user?.displayName?.split(' ')[0] || 'User'} {getRoleEmoji(user?.role)}
            </h2>
            <p style={{
                fontSize: theme.typography.body.fontSize,
              margin: 0,
              opacity: 0.9,
              lineHeight: '1.4',
              backgroundColor: 'transparent'
            }}>
              Ready to crush your wellness goals today?
            </p>
          </div>
          <button
            onClick={() => navigate('/join-challenges')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '10px',
              padding: isMobile ? '8px 16px' : '12px 24px',
              fontSize: theme.typography.button.fontSize,
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap',
              fontFamily: theme.fonts.primary,
              width: isMobile ? '100%' : 'auto',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🚀 Join New Challenges
          </button>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: isMobile ? '6px' : '16px',
          marginBottom: isMobile ? '12px' : '24px',
        }}>
          <div
            onClick={() => navigate('/join-challenges')}
            style={{
              backgroundColor: theme.colors.surface,
              padding: isMobile ? '10px 6px' : '16px',
              borderRadius: isMobile ? '10px' : '16px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
            }}
          >
            <div style={{
              fontSize: isMobile ? '20px' : '32px',
              fontWeight: '700',
              color: theme.colors.primary,
              marginBottom: isMobile ? '4px' : '8px'
            }}>
              {stats.available}
            </div>
            <div style={{
              fontSize: isMobile ? '10px' : '14px',
              fontWeight: '600',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Available
            </div>
          </div>

          <div
            onClick={() => {
              const wasAlreadyExpanded = currentExpanded;
              setCurrentExpanded(true);

              // Immediate native scroll without JavaScript timeouts
              if (scrollViewRef.current && challengesSectionRef.current) {
                // Calculate position based on content and accordion state
                const basePosition = 400; // Base position for challenges section
                const accordionHeaderHeight = 50; // Approximate accordion header height

                // Calculate target position for accordion section
                const targetPosition = basePosition + (wasAlreadyExpanded ? 0 : accordionHeaderHeight);

                // Immediate native scroll call
                scrollViewRef.current.scrollTo({
                  top: targetPosition,
                  behavior: 'smooth'
                });
                console.log('Immediate native scroll to accordion section at position:', targetPosition);
              }
            }}
            ref={challengesSectionRef}
            style={{
              backgroundColor: theme.colors.surface,
              padding: isMobile ? '10px 6px' : '16px',
              borderRadius: isMobile ? '10px' : '16px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
            }}
          >
            <div style={{
              fontSize: isMobile ? '20px' : '32px',
              fontWeight: '700',
              color: theme.colors.success,
              marginBottom: isMobile ? '4px' : '8px'
            }}>
              {stats.active}
            </div>
            <div style={{
              fontSize: isMobile ? '10px' : '14px',
              fontWeight: '600',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Active
            </div>
          </div>

          <div style={{
            backgroundColor: theme.colors.surface,
            padding: isMobile ? '10px 6px' : '16px',
            borderRadius: isMobile ? '10px' : '16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          }}>
            <div style={{
              fontSize: isMobile ? '20px' : '32px',
              fontWeight: '700',
              color: theme.colors.warning,
              marginBottom: isMobile ? '4px' : '8px'
            }}>
              {stats.streak}
            </div>
            <div style={{
              fontSize: isMobile ? '10px' : '14px',
              fontWeight: '600',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Day Streak
            </div>
          </div>
        </div>

        {/* Current Challenges Section */}
        <div ref={challengesSectionRef}>
          <Accordion
            title="Your Current Challenges"
            badge={activeChallenges.length}
            expanded={currentExpanded}
            onToggle={setCurrentExpanded}
          >
            {activeChallenges.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : isLargeScreen ? 'repeat(2, 1fr)' : '1fr',
                gap: isMobile ? '8px' : '16px',
              }}>
                {activeChallenges.map((challenge) => (
                  <ChallengeCard
                    key={challenge?.id || `challenge-${Math.random()}`}
                    challenge={challenge}
                    isActive={true}
                    progress={userParticipations[challenge.id]?.progress || 0}
                    participation={userParticipations[challenge.id]}
                    onJoin={handleJoinChallenge}
                    onViewProgress={handleViewProgress}
                    onVideoPlayOverride={handleCardVideoPlay}
                  />
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '48px 24px',
                backgroundColor: theme.colors.surface,
                borderRadius: '16px',
                marginTop: '8px',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: theme.colors.textSecondary,
                  marginBottom: '8px'
                }}>
                  No active challenges
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textTertiary,
                  lineHeight: '1.5'
                }}>
                  Join a challenge to get started!
                </div>
              </div>
            )}
          </Accordion>
        </div>

        {/* Quick Filters */}
        <div style={{ marginBottom: isMobile ? '12px' : '24px' }}>
          <h3 style={{
            fontSize: isMobile ? '12px' : '16px',
            fontWeight: '600',
            marginBottom: isMobile ? '8px' : '12px',
            color: theme.colors.text
          }}>
            Find New Challenges
          </h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: isMobile ? '4px' : '8px',
          }}>
            <button
              onClick={() => setSelectedFilter('all')}
              style={{
                padding: isMobile ? '4px 10px' : '8px 16px',
                borderRadius: '20px',
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: selectedFilter === 'all' ? theme.colors.primary : theme.colors.surface,
                color: selectedFilter === 'all' ? theme.colors.primaryText : theme.colors.text,
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              All
            </button>

            <button
              onClick={() => setSelectedFilter('global')}
              style={{
                padding: isMobile ? '4px 10px' : '8px 16px',
                borderRadius: '20px',
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: selectedFilter === 'global' ? theme.colors.primary : theme.colors.surface,
                color: selectedFilter === 'global' ? theme.colors.primaryText : theme.colors.text,
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              🌍 Global
            </button>

            <button
              onClick={() => setSelectedFilter('personal')}
              style={{
                padding: isMobile ? '4px 10px' : '8px 16px',
                borderRadius: '20px',
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: selectedFilter === 'personal' ? theme.colors.primary : theme.colors.surface,
                color: selectedFilter === 'personal' ? theme.colors.primaryText : theme.colors.text,
                fontSize: isMobile ? '11px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              👤 Personal
            </button>

            {hasOrganization && (
              <button
                onClick={() => setSelectedFilter('organization')}
                style={{
                  padding: isMobile ? '4px 10px' : '8px 16px',
                  borderRadius: '20px',
                  border: `1px solid ${theme.colors.border}`,
                  backgroundColor: selectedFilter === 'organization' ? theme.colors.primary : theme.colors.surface,
                  color: selectedFilter === 'organization' ? theme.colors.primaryText : theme.colors.text,
                  fontSize: isMobile ? '11px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                🏢 Organization
              </button>
            )}
          </div>
        </div>

        {/* Recommendations Section */}
        <Accordion
          title="Recommended For You"
          badge={filteredRecommendations.length}
          expanded={recommendationsExpanded}
          onToggle={setRecommendationsExpanded}
        >
          {filteredRecommendations.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : isLargeScreen ? 'repeat(2, 1fr)' : '1fr',
              gap: isMobile ? '8px' : '16px',
            }}>
              {filteredRecommendations.map((challenge) => (
                <ChallengeCard
                  key={challenge?.id || `challenge-${Math.random()}`}
                  challenge={challenge}
                  onJoin={handleJoinChallenge}
                  onViewProgress={handleViewProgress}
                  onVideoPlayOverride={handleCardVideoPlay}
                />
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              backgroundColor: theme.colors.surface,
              borderRadius: '16px',
              marginTop: '8px',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: theme.colors.textSecondary,
                marginBottom: '8px'
              }}>
                No challenges available
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textTertiary,
                lineHeight: '1.5'
              }}>
                {selectedFilter === 'all'
                  ? 'You\'ve joined all available challenges!'
                  : `No ${selectedFilter} challenges available`}
              </div>
            </div>
          )}
        </Accordion>


      </main>

      {/* Dashboard-level Video / Audio Player Modal (shared with ChallengeProgress view style) */}
      {showVideoPlayer && videoChallenge && ((videoChallenge as any).videoUrl || (videoChallenge as any).audioUrl) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {(videoChallenge as any).videoUrl ? (
            <VideoPlayer
              videoUrl={(videoChallenge as any).videoUrl}
              onClose={handleDashboardVideoClose}
              autoPlay={true}
              showControls={true}
              className="w-full h-full"
            />
          ) : (
            <div style={{
              backgroundColor: '#000000',
              padding: '24px',
              borderRadius: '16px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
              color: '#ffffff',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                  Challenge Audio
                </h3>
                <button
                  onClick={handleDashboardVideoClose}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '20px',
                    cursor: 'pointer',
                  }}
                  aria-label="Close audio"
                >
                  ×
                </button>
              </div>
              <p style={{ marginBottom: '12px', fontSize: '14px', opacity: 0.85 }}>
                Listen to the challenge instructions or motivation.
              </p>
              <audio
                src={(videoChallenge as any).audioUrl}
                controls
                autoPlay
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>
      )}

      {/* Logging Configuration Modal */}
      <LoggingConfigModal
        isVisible={showLoggingConfig}
        onClose={() => setShowLoggingConfig(false)}
      />
    </div>
  );
};

export default HomeScreen; 